import { useState } from "react";
import { axiosInstance } from "../utils/axios";
import useModalStore from "../stores/useModalStore";
import {
  ExclamationTriangleIcon,
  ArrowUpTrayIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import useDialogStore from "../stores/useDialogStore";
import DragDropUpload from "./DragDropUpload";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import Button from "./Button";
import { useEffect } from "react";

export default function ImportDevicesModal() {
  const [file, setFile] = useState(null);
  const [message, setMessage] = useState("");
  const [importData, setImportData] = useState(null);
  const [hasErrors, setHasErrors] = useState(false);
  const [errorData, setErrorData] = useState(null);
  const [allowImport, setAllowImport] = useState(false);
  const { setShowLoading } = useModalStore();
  const { closeDialog } = useDialogStore();
  const queryClient = useQueryClient();

  const importDevicesMutation = useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append("file", file);
      if (allowImport) {
        formData.append("force", true);
      }
      console.log("formData", formData);

      const response = await axiosInstance.post(
        `${window.REACT_APP_API_ENDPOINT}/export-excel/import-devices`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      return response.data;
    },
    onSuccess: (data) => {
      console.log("data", data);
      if (data.errors && data.errors.length > 0) {
        // Store error data and show errors with proceed option
        setErrorData(data);
        setHasErrors(true);
        const errorMessage = (
          <div className="mb-4">
            {/* <p className="text-green-600 font-semibold mb-2">{data.message}</p> */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="text-red-600">
                <p className="font-semibold mb-2">Validation Errors:</p>
                <ul className="list-disc list-inside space-y-1 max-h-60 overflow-y-auto">
                  {data.errors.map((error, index) => (
                    <li key={index} className="text-sm">
                      {error}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        );
        setMessage(errorMessage);
      } else {
        setImportData(data);
        setHasErrors(false);
        setErrorData(null);
        toast.success(data.message);
        queryClient.invalidateQueries(["devices"]);
      }
    },
    onError: (error) => {
      console.log("Import devices error:", error);
      const errorMessage = (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 w-full mb-4">
          <div className="text-red-600">
            <p className="font-semibold mb-2">Validation Errors:</p>
            <ul className="list-disc list-inside space-y-1 max-h-60 overflow-y-auto">
              {error.response?.data?.errors.map((error, index) => (
                <li key={index} className="text-sm">
                  {error}
                </li>
              ))}
            </ul>
          </div>
        </div>
      );
      setMessage(errorMessage);
      toast.error(error.response?.data?.message);
    },
  });

  const handleFileSelect = (selectedFile) => {
    setFile(selectedFile);
    setMessage(""); // Clear any previous messages
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!file) {
      setMessage("Please select a file.");
      return;
    }
    console.log("i am here");

    setShowLoading(true);
    importDevicesMutation.mutate(file, {
      onSettled: () => {
        setShowLoading(false);
      },
    });
  };

  const handleClearFile = () => {
    setFile(null);
    setMessage("");
  };

  const navigate = useNavigate();
  const pdfRedirect = () => {
    const ids = importData.devices.map((device) => device.id).join(",");
    navigate(`/devices/multi-device-details?ids=${ids}`);
    closeDialog();
  };

  const handleProceedWithErrors = () => {
    if (errorData) {
      setImportData(errorData);
      setHasErrors(false);
      setErrorData(null);
      setAllowImport(true);
    }
  };

  const isUploading = importDevicesMutation.isPending;

  useEffect(() => {
    setErrorData(null);
    setHasErrors(false);
  }, [file]);

  return (
    <div className="relative ">
      <form onSubmit={handleSubmit}>
        <div className="w-full flex flex-col justify-between items-center py-3 px-4 bg-white rounded-lg">
          <div className="w-full flex flex-col gap-4 mb-4">
            <div className="flex items-center justify-center w-full">
              <DragDropUpload
                onFileSelect={handleFileSelect}
                accept=".csv,.xls,.xlsx"
                maxSize={10 * 1024 * 1024} // 10MB for Excel files
                className="h-32"
                hasFile={!!file}
              >
                {!file ? (
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <svg
                      className="w-8 h-8 text-bluePrimary"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 20 16"
                    >
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                      />
                    </svg>
                    <p className="mb-2 text-sm text-gray-500">
                      <span className="font-semibold">Click to upload</span> or
                      drag and drop
                    </p>
                    <p className="text-xs text-gray-500">
                      CSV or Excel files only
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <svg
                      className="w-8 h-8 text-green-500"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <p className="mb-2 text-sm font-semibold text-green-600">
                      File uploaded successfully
                    </p>
                    <p className="text-xs text-gray-500">
                      {file.name} ({(file.size / 1024).toFixed(2)} KB)
                    </p>
                    <p
                      className="text-xs text-blue-500 mt-2 cursor-pointer hover:underline"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClearFile();
                      }}
                    >
                      Change file
                    </p>
                  </div>
                )}
              </DragDropUpload>
            </div>
          </div>
          {message && (
            <div className="w-full">
              {typeof message === "string" ? (
                <p className="text-sm font-semibold w-full">{message}</p>
              ) : (
                message
              )}
            </div>
          )}

          {/* PDF Download Button - Only show when import is successful */}
          {importData && !hasErrors && (
            <Button
              variant="outline-danger"
              size="md"
              onClick={pdfRedirect}
              className="mb-4"
              icon={
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              }
            >
              Download Imported Devices PDF
            </Button>
          )}
          <div className="flex justify-end gap-3 w-full">
            <Button
              variant="outline-light"
              size="sm"
              onClick={closeDialog}
              disabled={isUploading}
            >
              Cancel
            </Button>
            {errorData?.requiresConfirmation ? (
              <Button
                variant="outline-warning"
                size="sm"
                onClick={handleProceedWithErrors}
                icon={<ExclamationTriangleIcon className="w-4 h-4" />}
              >
                Continue with Errors
              </Button>
            ) : (
              <Button
                variant="primary"
                size="sm"
                type="submit"
                icon={<ArrowUpTrayIcon className="w-4 h-4" />}
                disabled={!file || isUploading}
                loading={isUploading}
              >
                {isUploading ? "Uploading..." : "Upload"}
              </Button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
